<template>
  <div class="color-size-tab">
    <div class="tab-header">
      <div class="header-left">
        <span class="quantity-info">
          발주수량: <strong>{{ formatNumber(orderQuantity) }}</strong>
          &nbsp;|&nbsp;
          매트릭스 합계: <strong :class="{ 'mismatch': matrixTotal !== orderQuantity }">{{ formatNumber(matrixTotal) }}</strong>
          <template v-if="spareTotal > 0">
            &nbsp;|&nbsp;
            여유분: <strong class="spare-text">+{{ spareTotal }}</strong>
            → 총 생산: <strong>{{ formatNumber(matrixTotal + spareTotal) }}</strong>
          </template>
        </span>
        <el-tag v-if="matrixTotal !== orderQuantity && matrixTotal > 0" type="warning" size="small">
          차이: {{ formatNumber(matrixTotal - orderQuantity) }}
        </el-tag>
      </div>
      <div v-if="canEdit" class="header-right">
        <!-- 업무 순서: 요척(원단·색상 구성)이 먼저 확정 → 그 색상을 참조해
             수량배분 (2026-08-01, 사이먼). 색상 행을 손으로 다시 치지 않는다 -->
        <el-button size="small" :loading="importingColors" @click="importColorsFromYardage">요척 색상 가져오기</el-button>
        <el-button size="small" @click="addSize">사이즈 추가</el-button>
        <el-button size="small" @click="addColor">컬러 추가</el-button>
        <el-button type="primary" size="small" @click="saveMatrix" :loading="saving">저장</el-button>
      </div>
    </div>

    <!-- 스프레드시트형 매트릭스 -->
    <div class="matrix-wrapper" v-if="colors.length > 0 && sizes.length > 0">
      <table class="matrix-table">
        <thead>
          <tr>
            <th class="color-header">컬러 \ 사이즈</th>
            <th v-for="(size, si) in sizes" :key="si" class="size-header">
              {{ size }}
              <el-button
                v-if="canEdit"
                size="small"
                type="danger"
                link
                @click="removeSize(si)"
                class="remove-btn"
              >×</el-button>
            </th>
            <th class="total-header">합계</th>
            <th class="spare-header">여유분</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(color, ci) in colors" :key="ci">
            <td class="color-cell">
              {{ color }}
              <el-button
                v-if="canEdit"
                size="small"
                type="danger"
                link
                @click="removeColor(ci)"
                class="remove-btn"
              >×</el-button>
            </td>
            <!-- 셀 단위 여유분 (2026-08-01, 사이먼) — 종이 작지처럼 여유분이
                 특정 색상×사이즈(기준 사이즈)에 붙음: "1140+3". 편집 시 수량
                 옆 + 소칸, 조회 시 "1,140+3" 표기 -->
            <td v-for="(size, si) in sizes" :key="si" class="qty-cell">
              <template v-if="canEdit">
                <el-input-number
                  v-model="matrix[ci][si]"
                  :min="0"
                  :controls="false"
                  size="small"
                  class="qty-input"
                />
                <el-input-number
                  v-model="spareM[ci][si]"
                  :min="0"
                  :controls="false"
                  size="small"
                  class="spare-input"
                  :class="{ 'spare-filled': spareM[ci][si] > 0 }"
                  title="여유분 — 기준 사이즈 셀에 +3 처럼 입력"
                />
              </template>
              <span v-else>
                {{ formatNumber(matrix[ci][si]) }}<span v-if="spareM[ci][si]" class="spare-text">+{{ spareM[ci][si] }}</span>
              </span>
            </td>
            <td class="total-cell">
              {{ formatNumber(rowTotal(ci)) }}<span v-if="rowSpare(ci)" class="spare-text"> +{{ rowSpare(ci) }}</span>
            </td>
            <td class="qty-cell spare-cell">
              <span class="spare-text">{{ rowSpare(ci) ? '+' + rowSpare(ci) : '-' }}</span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td class="color-cell"><strong>합계</strong></td>
            <td v-for="(size, si) in sizes" :key="si" class="total-cell">
              {{ formatNumber(colTotal(si)) }}
            </td>
            <!-- 종이 작지 하단 "6,500 +12" 표기 재현 -->
            <td class="grand-total">
              {{ formatNumber(matrixTotal) }}<span v-if="spareTotal" class="spare-text"> +{{ spareTotal }}</span>
            </td>
            <td class="grand-total spare-text">{{ spareTotal ? '+' + spareTotal : '-' }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <el-empty v-else description="컬러/사이즈를 추가하여 매트릭스를 구성하세요" />

    <!-- 완제품 수지 (2026-07-29) — 색상×사이즈 잔여, 저장 0 파생 뷰 -->
    <!-- 실적(완제품입고·출고)이 생긴 뒤에만 표시 — 작성 단계에선 추정치뿐이라
         화면 소음 (2026-08-01 사이먼 지적) -->
    <div v-if="balance && balance.rows.length && (balance.totals.inbound > 0 || balance.totals.outbound > 0)" class="fg-balance">
      <div class="fg-header">
        <h4>완제품 수지 (색상×사이즈)</h4>
        <el-tag size="small" :type="balance.basis === 'ACTUAL' ? 'success' : 'info'">
          {{ balance.basis_label }}
        </el-tag>
      </div>
      <el-table :data="balance.rows" size="small" border stripe>
        <el-table-column prop="color" label="색상" min-width="100" />
        <el-table-column prop="size" label="사이즈" width="80" align="center" />
        <el-table-column label="생산계획" width="110" align="right">
          <template #default="{ row }">
            {{ formatNumber(row.planned) }}<span v-if="row.spare" class="spare-text">+{{ row.spare }}</span>
          </template>
        </el-table-column>
        <el-table-column label="완제품입고" width="100" align="right">
          <template #default="{ row }">{{ row.inbound ? formatNumber(row.inbound) : '-' }}</template>
        </el-table-column>
        <el-table-column label="출고 (납품 등)" width="110" align="right">
          <template #default="{ row }">{{ row.outbound ? formatNumber(row.outbound) : '-' }}</template>
        </el-table-column>
        <el-table-column label="잔여" width="100" align="right">
          <template #default="{ row }">
            <span :class="{ 'balance-pos': row.balance > 0, 'balance-neg': row.balance < 0 }">
              {{ formatNumber(row.balance) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
      <div class="fg-total">
        합계 — 생산계획 {{ formatNumber(balance.totals.planned) }}
        · 입고 {{ formatNumber(balance.totals.inbound) }}
        · 출고 {{ formatNumber(balance.totals.outbound) }}
        · <b>잔여 {{ formatNumber(balance.totals.balance) }}</b>
        <span v-if="balance.basis === 'PLAN'" class="fg-hint">
          — 완제품 입고를 입출고 화면에 기록하면 실측 기준으로 전환됩니다
        </span>
      </div>
    </div>

    <!-- 사이즈 추가 다이얼로그 -->
    <el-dialog v-model="showSizeDialog" title="사이즈 추가" width="350px">
      <el-form label-width="60px">
        <el-form-item label="사이즈">
          <el-input v-model="newSize" placeholder="예: XL, 2XL, FREE" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showSizeDialog = false">취소</el-button>
        <el-button type="primary" @click="confirmAddSize">추가</el-button>
      </template>
    </el-dialog>

    <!-- 컬러 추가 다이얼로그 -->
    <el-dialog v-model="showColorDialog" title="컬러 추가" width="350px">
      <el-form label-width="60px">
        <el-form-item label="컬러명">
          <el-input v-model="newColor" placeholder="예: Black, Navy" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showColorDialog = false">취소</el-button>
        <el-button type="primary" @click="confirmAddColor">추가</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  mainRequestId: { type: [String, Number], required: true },
  orderQuantity: { type: Number, default: 0 },
  canEdit: { type: Boolean, default: false }
})

const emit = defineEmits(['updated'])

const loading = ref(false)
const saving = ref(false)

// 매트릭스 데이터
const colors = ref([])
const sizes = ref([])
const matrix = ref([])  // matrix[colorIndex][sizeIndex] = quantity
// 셀 단위 여유분 (2026-08-01, 사이먼) — 종이 작지 "1140+3"과 동일하게
// 여유분이 특정 색상×사이즈에 귀속 (구버전: 색상당 1개 → 첫 셀에 임의 저장돼
// PDF에서 기준 사이즈가 아닌 셀에 +N이 찍히던 문제 해소)
const spareM = ref([])  // spareM[colorIndex][sizeIndex] = spare quantity

// 다이얼로그
const showSizeDialog = ref(false)
const showColorDialog = ref(false)
const newSize = ref('')
const newColor = ref('')

// 합계 계산
const rowTotal = (ci) => {
  return (matrix.value[ci] || []).reduce((sum, val) => sum + (val || 0), 0)
}

const rowSpare = (ci) => {
  return (spareM.value[ci] || []).reduce((sum, val) => sum + (val || 0), 0)
}

const colTotal = (si) => {
  return matrix.value.reduce((sum, row) => sum + (row[si] || 0), 0)
}

const matrixTotal = computed(() => {
  return matrix.value.reduce((sum, row) => {
    return sum + row.reduce((rSum, val) => rSum + (val || 0), 0)
  }, 0)
})

const spareTotal = computed(() => {
  return spareM.value.reduce((sum, row) => sum + row.reduce((s, v) => s + (v || 0), 0), 0)
})

const formatNumber = (num) => {
  return (num || 0).toLocaleString()
}

// 완제품 수지 (2026-07-29) — 색상×사이즈 잔여
const balance = ref(null)
const fetchBalance = async () => {
  if (!props.mainRequestId) return
  try {
    const res = await axiosInstance.get(
      `/production/main-requests/${props.mainRequestId}/finished-goods-balance/`
    )
    balance.value = res.data
  } catch (e) {
    console.error('완제품 수지 조회 실패:', e)
  }
}

// 데이터 로드
const fetchData = async () => {
  // 상세 화면 이탈 시 route param 이 undefined 로 바뀌며 watch 가 발화 —
  // /main-requests/undefined/size-quantities/ 404 방지 (2026-07-15)
  if (!props.mainRequestId) return
  loading.value = true
  try {
    const res = await axiosInstance.get(
      `/production/main-requests/${props.mainRequestId}/size-quantities/`
    )
    const data = res.data

    if (data.items && data.items.length > 0) {
      // 서버에서 가져온 데이터로 매트릭스 구성
      const uniqueColors = data.colors || [...new Set(data.items.map(i => i.color_name))]
      const uniqueSizes = data.sizes || [...new Set(data.items.map(i => i.size))]

      colors.value = uniqueColors
      sizes.value = uniqueSizes

      // 빈 매트릭스 초기화
      matrix.value = uniqueColors.map(() => uniqueSizes.map(() => 0))
      spareM.value = uniqueColors.map(() => uniqueSizes.map(() => 0))

      // 데이터 채우기 — 여유분은 저장된 셀 그대로 (셀 귀속)
      for (const item of data.items) {
        const ci = uniqueColors.indexOf(item.color_name)
        const si = uniqueSizes.indexOf(item.size)
        if (ci >= 0 && si >= 0) {
          matrix.value[ci][si] = item.quantity
          if (item.spare_quantity) spareM.value[ci][si] = item.spare_quantity
        }
      }
    } else {
      colors.value = []
      sizes.value = []
      matrix.value = []
      spareM.value = []
    }
  } catch (error) {
    console.error('Failed to fetch size quantities:', error)
  } finally {
    loading.value = false
  }
}

// 요척 색상 가져오기 (2026-08-01) — 요척 탭에 등록된 원단 색상을 행으로 추가.
// 업무 순서(요척 확정 → 수량배분)를 화면 동선으로 지원 + 색상명 불일치 예방
const importingColors = ref(false)
const importColorsFromYardage = async () => {
  importingColors.value = true
  try {
    const res = await axiosInstance.get(
      'production/yardages/', { params: { main_request: props.mainRequestId } })
    const rows = Array.isArray(res.data) ? res.data : res.data.results || []
    const yColors = [...new Set(rows.map(r => (r.color || '').trim()).filter(Boolean))]
    if (!yColors.length) {
      ElMessage.info('요척 탭에 색상이 등록된 원단이 없습니다. 요척을 먼저 입력하세요.')
      return
    }
    const added = []
    for (const c of yColors) {
      if (!colors.value.includes(c)) {
        colors.value.push(c)
        matrix.value.push(sizes.value.map(() => 0))
        spareM.value.push(sizes.value.map(() => 0))
        added.push(c)
      }
    }
    ElMessage.success(added.length
      ? `요척에서 색상 ${added.length}건 추가: ${added.join(', ')} — 수량 입력 후 저장하세요.`
      : '요척의 색상이 모두 이미 매트릭스에 있습니다.')
  } catch (e) {
    console.error('요척 색상 가져오기 실패:', e)
    ElMessage.error('요척 색상 가져오기에 실패했습니다.')
  } finally {
    importingColors.value = false
  }
}

// 사이즈 추가
const addSize = () => {
  newSize.value = ''
  showSizeDialog.value = true
}

const confirmAddSize = () => {
  const size = newSize.value.trim()
  if (!size) {
    ElMessage.warning('사이즈를 입력하세요.')
    return
  }
  if (sizes.value.includes(size)) {
    ElMessage.warning('이미 존재하는 사이즈입니다.')
    return
  }
  sizes.value.push(size)
  // 각 컬러 행에 0 추가
  for (const row of matrix.value) {
    row.push(0)
  }
  for (const row of spareM.value) {
    row.push(0)
  }
  showSizeDialog.value = false
}

// 컬러 추가
const addColor = () => {
  newColor.value = ''
  showColorDialog.value = true
}

const confirmAddColor = () => {
  const color = newColor.value.trim()
  if (!color) {
    ElMessage.warning('컬러명을 입력하세요.')
    return
  }
  if (colors.value.includes(color)) {
    ElMessage.warning('이미 존재하는 컬러입니다.')
    return
  }
  colors.value.push(color)
  matrix.value.push(sizes.value.map(() => 0))
  spareM.value.push(sizes.value.map(() => 0))
  showColorDialog.value = false
}

// 사이즈 삭제
const removeSize = async (si) => {
  try {
    await ElMessageBox.confirm(`"${sizes.value[si]}" 사이즈를 삭제하시겠습니까?`, '확인')
    sizes.value.splice(si, 1)
    for (const row of matrix.value) {
      row.splice(si, 1)
    }
    for (const row of spareM.value) {
      row.splice(si, 1)
    }
  } catch {
    // cancelled
  }
}

// 컬러 삭제
const removeColor = async (ci) => {
  try {
    await ElMessageBox.confirm(`"${colors.value[ci]}" 컬러를 삭제하시겠습니까?`, '확인')
    colors.value.splice(ci, 1)
    matrix.value.splice(ci, 1)
    spareM.value.splice(ci, 1)
  } catch {
    // cancelled
  }
}

// 저장
const saveMatrix = async () => {
  saving.value = true
  try {
    const items = []
    for (let ci = 0; ci < colors.value.length; ci++) {
      for (let si = 0; si < sizes.value.length; si++) {
        const qty = matrix.value[ci][si] || 0
        const spare = (spareM.value[ci] || [])[si] || 0
        // 여유분은 입력한 셀에 그대로 귀속 저장 — PDF "1140+3"이 정확한
        // 기준 사이즈 셀에 찍힘 (2026-08-01)
        if (qty > 0 || spare > 0) {
          items.push({
            color_name: colors.value[ci],
            size: sizes.value[si],
            quantity: qty,
            spare_quantity: spare
          })
        }
      }
    }

    await axiosInstance.post(
      `/production/main-requests/${props.mainRequestId}/size-quantities/`,
      { items }
    )
    ElMessage.success('색상×사이즈 매트릭스가 저장되었습니다.')
    emit('updated')
  } catch (error) {
    ElMessage.error('저장 중 오류가 발생했습니다.')
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  fetchData()
  fetchBalance()
})

watch(() => props.mainRequestId, () => {
  fetchData()
  fetchBalance()
})
</script>

<style scoped>
.color-size-tab {
  padding: 20px;
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-right {
  display: flex;
  gap: 8px;
}

.quantity-info {
  font-size: 13px;
  color: #606266;
}

.mismatch {
  color: #E6A23C;
}

/* 매트릭스 테이블 */
.matrix-wrapper {
  overflow-x: auto;
}

.matrix-table {
  border-collapse: collapse;
  width: auto;
  min-width: 100%;
}

.matrix-table th,
.matrix-table td {
  border: 1px solid #EBEEF5;
  padding: 6px 8px;
  text-align: center;
  font-size: 13px;
}

.matrix-table thead th {
  background: #F5F7FA;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
}

.color-header {
  min-width: 120px;
  text-align: left !important;
}

.size-header {
  min-width: 80px;
  position: relative;
}

.total-header {
  min-width: 80px;
  background: #E6F0FF !important;
}

.spare-header {
  min-width: 80px;
  background: #FDF6EC !important;
}

.spare-cell {
  background: #FDF6EC;
}

.spare-text {
  color: #E6A23C;
  font-weight: 600;
}

/* 완제품 수지 */
.fg-balance {
  margin-top: 24px;
}

.fg-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.fg-header h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

.fg-total {
  margin-top: 8px;
  font-size: 13px;
  color: #606266;
}

.fg-hint {
  color: #909399;
  font-size: 12px;
}

.balance-pos {
  color: #E6A23C;
  font-weight: 700;
}

.balance-neg {
  color: #F56C6C;
  font-weight: 700;
}

.color-cell {
  text-align: left !important;
  font-weight: 500;
  white-space: nowrap;
  position: relative;
}

.qty-cell {
  padding: 2px 4px;
}

.qty-input {
  width: 64px;
}

.qty-input :deep(.el-input__inner) {
  text-align: center;
}

/* 셀 단위 여유분 소칸 (2026-08-01) — 평소 흐리게, 값 있으면 주황 강조 */
.spare-input {
  width: 42px;
  margin-left: 2px;
  opacity: 0.55;
}
.spare-input:focus-within,
.spare-input.spare-filled {
  opacity: 1;
}
.spare-input :deep(.el-input__inner) {
  text-align: center;
}
.spare-input.spare-filled :deep(.el-input__inner) {
  color: #e6a23c;
  font-weight: 700;
}

.total-cell {
  background: #F5F7FA;
  font-weight: 600;
}

.grand-total {
  background: #E6F0FF;
  font-weight: 700;
  font-size: 14px;
}

.remove-btn {
  padding: 0 2px;
  font-size: 14px;
  margin-left: 4px;
}

tfoot td {
  background: #F5F7FA;
  font-weight: 600;
}
</style>
